<template>
  <div
    v-if="tabs.length"
    ref="root"
    class="ui-tabs"
  >
    <div
      ref="marker"
      :style="{
        top: `${markerOffset}px`,
      }"
      class="ui-tabs-scroll-marker"
    />

    <div
      v-if="!accordion"
      class="ui-tabs-wrapper"
    >
      <ul ref="tabs" class="ui-tabs-tabs">
        <li
          v-for="tab in tabs"
          :key="tab.key"
          :class="{ active: tab.active }"
          @click="onClickTab(tab.key)"
        >
          {{ tab.name }}
          <ui-badge
            :text="tab.badge"
            color="#333"
          />
          <div class="underline" />
        </li>
      </ul>

      <div v-show="rightGradientIsVisible" class="ui-tabs-gradient" />
      <div v-show="leftGradientIsVisible" class="ui-tabs-gradient left" />
    </div>

    <div :class="['ui-tabs-content', currentTab.contentClass]">
      <!-- eslint-disable vue/no-v-html -->
      <component
        :is="currentTab.component"
        v-if="currentTab.component"
        @tabs:scroll-to-marker="scrollToMarker"
      />
      <div
        v-else-if="currentTab.content"
        class="html-content"
        v-html="sanitize(currentTab.content)"
      />
      <!-- eslint-enable vue/no-v-html -->
    </div>
  </div>
</template>

<script lang="ts">
import { debounce } from 'throttle-debounce';
import { defineComponent, inject, PropType } from 'vue';

import cleanObject from '@/utils/helpers/cleanObject';
import { sanitize } from '@/utils/sanitize';
import { scrollToElement } from '@/utils/scroll-to-element';
import type { IAdaptive } from '@/utils/system/adaptive';

import type { IUiTabItem } from './types';
import { useTabs } from './useTabs';

export default defineComponent({
  name: 'ui-tabs',

  props: {
    items: {
      type: Array as PropType<IUiTabItem[]>,
      default: () => [],
    },

    modelValue: { // дефолтное значение, если нет хэша
      type: String,
      required: true,
    },

    accordion: {
      type: Boolean,
      default: false,
    },

    markerOffset: {
      type: Number,
      default: -65,
    },

    enableScroll: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'update:modelValue',
    'click-tab',
    'update:modelValue',
  ],

  setup() {
    const adaptive = inject<IAdaptive>('adaptive');
    const tabUtils = useTabs('.ui-tabs-tabs');

    return {
      adaptive,
      sanitize,
      tabUtils,
    };
  },

  data() {
    return {
      isMounted: false,
      rightGradientIsVisible: true,
      leftGradientIsVisible: false,
    };
  },

  computed: {
    // ключ хэша с которым работаем
    urlHash: {
      set(k: string) {
        this.$router.replace(cleanObject({
          name: this.$route.name,
          params: this.$route.params,
          query: this.$route.query,
          hash: `#${k}`,
        }));
        this.$emit('update:modelValue', k);
      },

      get(): string {
        if (this.delayedHash === 'register') {
          return '';
        }

        return this.delayedHash || this.modelValue;
      },
    },

    tabs(): IUiTabItem[] {
      return this.items.map((i) => ({
        ...i,
        active: i.key === this.urlHash,
      }));
    },

    activeIndex(): number {
      return this.tabs.findIndex(({ active }) => active) || 0;
    },

    currentTab(): IUiTabItem {
      return this.tabs[this.activeIndex] ?? {};
    },

    delayedHash(): string { // фиксит проблему вызова конкретного таба при SSR
      return this.isMounted ? this.$route?.hash?.slice(1) : '';
    },

    tabsWrapper(): HTMLElement | null {
      return (this.$refs?.tabs as HTMLElement) || null;
    },
  },

  watch: {
    '$route.hash': {
      handler(hash: string) {
        this.tabUtils.scrollMenuToActiveElement();

        if (!this.enableScroll || !hash) {
          return;
        }

        // review.vue скроллит сам после загрузки
        if (hash === '#review') {
          return;
        }

        this.scrollToMarker();
      },
      // запускает вотчер при старте компонента
      immediate: true,
    },
  },

  mounted() {
    this.isMounted = true;

    this.tabUtils.scrollMenuToActiveElement();

    if (!this.adaptive?.isMobile || !this.tabsWrapper) {
      return;
    }

    // TODO: Нет отписки от события
    this.tabsWrapper.addEventListener('scroll', debounce(20, () => {
      if (!this.tabsWrapper) {
        // так как находимся в коллбеке, делаем дополнительную проверку
        return;
      }

      const { scrollWidth, offsetWidth, scrollLeft } = this.tabsWrapper;
      this.rightGradientIsVisible = scrollWidth > offsetWidth
            && ((scrollWidth - (Math.ceil(scrollLeft)) !== offsetWidth));
      this.leftGradientIsVisible = scrollLeft > 0;
    }));
  },

  methods: {
    getMarkerElement(): HTMLElement | null {
      return (this.$refs.marker as HTMLElement) || null;
    },

    onClickTab(key: string) {
      this.urlHash = key;
      this.$emit('click-tab', key);
    },

    async scrollToMarker() {
      if (!this.getMarkerElement()) {
        // Элемент еще не появился, даем странице загрузиться
        await new Promise((ok) => { setTimeout(ok, 1000); });
      }
      if (this.urlHash && this.getMarkerElement()) {
        scrollToElement(this.getMarkerElement());
        this.$emit('update:modelValue', this.urlHash);
      }
    },
  },
});
</script>

<style lang="scss">
.ui-tabs {
  position: relative;

  &-scroll-marker {
    position: absolute;
  }

  &-wrapper {
    // оболочка, чтобы спрятать сроллбар в мобиле
    position: relative;
    height: 36px;
    overflow: hidden;
    &::after {
      @include stacking("behind");
      content: '';
      position: absolute;
      top: 35px;
      left: 0;
      right: 0;
      border-bottom: 1px solid $gray_lu;
    }

    ul {
      margin: 0;
    }
  }

  &-content {
    &.static-tab-content {
      padding-top: 24px;

      .html-content {
        // TODO: Исправить на использование БЭМ-селекторов
        // stylelint-disable-next-line selector-max-universal
        & > *:first-child {
          margin-top: 0;
        }
      }
    }
  }

  &-tabs {
    display: flex;
    position: relative;
    overflow-y: auto;
    color: $gray;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    scroll-behavior: smooth;

    @include hide-scroll;
  }

  &-tabs > li {
    position: relative;
    display: flex;
    align-items: center;
    padding: 8px 0;
    white-space: nowrap;

    &:not(:first-child) {
      margin-left: 24px;
    }

    .underline {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 0;
      height: 2px;
      background: $black;
      transition: .3s width ease;
    }

    &.active {
      color: $black;
      font-weight: 600;
      pointer-events: none;

      .underline {
        width: 100%;
      }
    }

    .ui-badge {
      margin-left: 8px;
    }
  }

  &-gradient {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 40px;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, $white 100%);

    &.left {
      right: auto;
      left: 0;
      background: linear-gradient(90deg, $white 0%, rgba(255, 255, 255, 0) 100%);
    }
  }
}

@include breakpoint("desktop") {
  .ui-tabs {
    &-wrapper {
      margin: 0;
      height: auto;
      overflow: hidden;
      &::after {
        top: 53px;
      }
    }

    &-content {
      &.static-tab-content {
        padding-top: 32px;
      }
    }
    &-tabs {
      padding: 0;
      font-size: 16px;
      line-height: 24px;
    }
    &-tabs > li {
      padding: 15px 0;
      cursor: pointer;
      transition: .2s ease color;
      &::after {
        content: '';
        position: absolute;
        display: block;
        bottom: 0;
        left: 0;
        right: 0;
        height: 2px;
        background-color: $gray;
        opacity: 0;
        transition: .2s opacity ease;
      }
      &:first-child::after {
        left: 0;
      }

      &:hover {
        color: $black;

        &::after {
          opacity: 1;
        }
      }
    }
  }
}
</style>
